/**
 * drawerBasis (HOF-467) — the Deckungsbeitrag drawer's OWN Bezugsgröße switcher over the
 * page's ``MarginBasis`` union. ``€ gesamt`` started here as a drawer-local mode (HOF-467
 * kept it out of the URL-borne page union on purpose); on 2026-09-07 the page adopted the
 * drawer's five, so ``DrawerBasis`` IS ``MarginBasis`` now and the two switchers offer the
 * same set. The drawer still initialises from the page's basis and then switches on its own.
 *
 * Every option is built from ONE fact: a denominator the fact does not carry renders the
 * option disabled with the basis' own "missing" noun (``BASIS_META``), and every value in
 * that basis a dash — never ``0,00 €/X`` (#2304's null-honesty, carried into the organism).
 */
import type { MarginBasisOption } from '@hofsync/design-system';
import { BASIS_META, BASIS_ORDER, basisQuantity, isMarginBasis, type MarginBasis } from './basis';
import { yieldUnitLabel } from './format';
import { perUnitQuantity } from './perUnit';
import type { DeckungsbeitragRow } from './types';
import type { KnownYieldUnit } from './yieldUnit';

export type DrawerBasis = MarginBasis;

/** Canonical order the drawer's switcher offers — the page's, € gesamt first. */
export const DRAWER_BASIS_ORDER: readonly DrawerBasis[] = BASIS_ORDER;

export const TOTAL_BASIS_LABEL = BASIS_META.total.label;

/**
 * The ``kg`` option reads in the fact's OWN yield unit (the #2844 rule ``perUnitQuantity``
 * carries): an arable harvest is ``je dt``, a berry pick ``je kg``. Keyed on the shared
 * ``KG_PER_YIELD_UNIT`` key set, so a unit added to that table fails ``tsc`` here until it
 * gets a switcher label (the #2930 lock-step, as ``PER_UNIT_HEADINGS`` had it).
 */
export const YIELD_BASIS_LABEL: Record<KnownYieldUnit, string> = {
  dt: 'je dt',
  t: 'je t',
  kg: BASIS_META.kg.label,
};

export function isDrawerBasis(v: unknown): v is DrawerBasis {
  return isMarginBasis(v);
}

/** Compact "€/…" unit suffix per basis — ``BASIS_META.unit`` for the geometry bases, the fact's yield unit for kg. */
const unitSuffix = (basis: MarginBasis, row: DeckungsbeitragRow): string =>
  basis === 'kg' ? `€/${yieldUnitLabel(row.yield_unit)}` : `€/${BASIS_META[basis].unit}`;

/** The drawer's denominator per basis: kg in the fact's own unit, everything else via ``basisQuantity``. */
const divisorFor = (basis: MarginBasis, row: DeckungsbeitragRow): number | null =>
  basis === 'kg' ? perUnitQuantity(row, 'kg') : basisQuantity(row, basis);

/** The five switcher options for one row, in ``DRAWER_BASIS_ORDER``. */
export function drawerBasisOptions(row: DeckungsbeitragRow): MarginBasisOption[] {
  return DRAWER_BASIS_ORDER.map((basis) => {
    if (basis === 'total') return { id: 'total', label: TOTAL_BASIS_LABEL, unit: '', divisor: 1 };
    const divisor = divisorFor(basis, row);
    return {
      id: basis,
      label: basis === 'kg' ? YIELD_BASIS_LABEL[yieldUnitLabel(row.yield_unit)] : BASIS_META[basis].label,
      unit: unitSuffix(basis, row),
      divisor,
      ...(divisor === null ? { disabledReason: `Keine ${BASIS_META[basis].missing} hinterlegt` } : {}),
    };
  });
}
